import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Card, CardContent, CardHeader, CardTitle } from "design-system/molecules/Card"
import { Bot, BotOff, Milestone, ShieldAlert, ShieldQuestion } from "lucide-react"
import { useCustomerLogsQuery } from "modules/customers/hooks/useCustomerLogsQuery"
import { useTranslation } from "react-i18next"
import { CustomerLogResponseDataSchema } from "schema"
import { z } from "zod"

type CustomerLog = z.infer<typeof CustomerLogResponseDataSchema>

type Props = {
  customerId: string
}

function CustomerLogContent({ log }: { log: CustomerLog }) {
  const { i18n, t } = useTranslation()
  const isAiAuditLog = log.kind === "AIDisabled" || log.kind === "AIResumed"
  const author = log.admin ?? log.employee
  const formattedTime = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(log.createdAt))

  let icon
  let label
  switch (log.kind) {
    case "AIDisabled":
      icon = <BotOff size={16} className="shrink-0" />
      label = t("conversations.chat.aiDisabled")
      break
    case "AIResumed":
      icon = <Bot size={16} className="shrink-0" />
      label = t("conversations.chat.aiResumed")
      break
    case "FraudDetected":
      icon = <ShieldAlert size={16} className="shrink-0" />
      label = t("conversations.chat.fraudDetected")
      break
    case "BrandRelatedFraudReported":
      icon = <ShieldQuestion size={16} className="shrink-0" />
      label = t("conversations.chat.brandRelatedFraudReported")
      break
    case "TrollingDetected":
      icon = <ShieldQuestion size={16} className="shrink-0" />
      label = t("conversations.chat.trollingDetected")
      break
  }

  if (!label) return null

  const content = (
    <>
      {icon}
      <span>{label}</span>
    </>
  )

  if (!isAiAuditLog) return <div className="flex items-center gap-2">{content}</div>

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center gap-2 text-left cursor-pointer">
          {content}
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-1.5 text-sm">
        {log.source === "automatic" && <p>{t("conversations.chat.aiAudit.automatic")}</p>}
        {log.source === "workflow" && <p>{t("conversations.chat.aiAudit.workflow")}</p>}
        {log.source && !author && log.source !== "automatic" && log.source !== "workflow" && (
          <p>{t("conversations.chat.aiAudit.integration")}</p>
        )}
        {!log.source && !author && <p>{t("conversations.chat.aiAudit.unattributed")}</p>}
        {author && <p>{t("conversations.chat.aiAudit.changedBy", { name: author.name })}</p>}
        <time className="self-end text-xs text-muted-foreground">{formattedTime}</time>
      </PopoverContent>
    </Popover>
  )
}

export function CustomerLogsTimeline({ customerId }: Props) {
  const { t } = useTranslation()
  const { data: customerLogs, isPending } = useCustomerLogsQuery(customerId)

  let timelineContent
  if (isPending) {
    timelineContent = <div className="h-20 animate-pulse rounded-lg bg-muted" />
  } else if (customerLogs?.length) {
    timelineContent = (
      <div className="relative">
        {customerLogs.map((log, index) => {
          const isLast = index === customerLogs.length - 1

          return (
            <div key={log.id} className="relative flex w-full min-h-14">
              <div className="absolute flex flex-col items-center left-4 top-0 h-full z-10">
                <div className="shrink-0 mt-1.5 size-2.5 rounded-full bg-muted-foreground/40" />
                {!isLast && <div className="w-0.5 bg-border flex-1" />}
              </div>
              <div className="flex flex-col gap-1 w-full pb-6 pl-12 pr-4 text-sm text-muted-foreground">
                <CustomerLogContent log={log} />
                <time className="text-xs text-muted-foreground">
                  {new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(log.createdAt))}
                </time>
              </div>
            </div>
          )
        })}
      </div>
    )
  } else {
    timelineContent = (
      <div className="flex flex-col gap-2 py-8 items-center justify-center text-center max-w-md mx-auto">
        <Milestone className="size-8 text-muted-foreground" />
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("customers.profile.logs.empty")}</span>
          <span className="text-sm text-muted-foreground">
            {t("customers.profile.logs.emptyDescription")}
          </span>
        </div>
      </div>
    )
  }

  return (
    <section className="flex flex-col gap-4 w-full">
      <Card>
        <CardHeader className="px-4 pt-3 pb-2">
          <CardTitle className="text-sm text-foreground">
            {t("customers.profile.logs.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">{timelineContent}</CardContent>
      </Card>
    </section>
  )
}
